import { asArray, asObject, parseJsonObject, text } from './model-utils.js';

export const DOCUMENT_SCHEMA_VERSION = 1;

export const CHAPTER_DEFINITIONS = [
  ['cover', '项目封面'],
  ['overview', '项目概况'],
  ['sources', '资料来源与解析范围'],
  ['constraints', '规划指标与约束'],
  ['site-analysis', '基地分析与设计切入点'],
  ['risks', '风险、缺失信息与待复核内容'],
  ['concept-options', '三个概念方案方向'],
  ['selected-concept', '最终采用方案'],
  ['masterplan-strategy', '总图布局与设计策略'],
  ['building-program', '建筑与功能清单'],
  ['metrics', '规模测算和主要指标'],
  ['visual-results', '效果表达成果'],
  ['conclusion', '结论与后续深化建议']
].map(([id, title], index) => ({ id, title, ordinal: index + 1 }));

/** @param {unknown} stages @returns {Record<string, {status: string, data: Record<string, any>, updatedAt?: string}>} */
export function normalizeStages(stages) {
  const rows = Array.isArray(stages)
    ? stages
    : Object.entries(asObject(stages)).map(([stage, value]) => ({ stage, ...asObject(value) }));
  return Object.fromEntries(rows.map(row => {
    const rawData = row.data ?? row.data_json ?? {};
    return [row.stage, {
      status: text(row.status, 'pending'),
      data: asObject(parseJsonObject(rawData)),
      updatedAt: row.updatedAt || row.updated_at || ''
    }];
  }));
}

/** @param {{status?: string} | undefined} stage @returns {boolean} */
export function isCompletedStage(stage) {
  return stage?.status === 'completed' || stage?.status === 'confirmed';
}

/**
 * @param {string} id
 * @param {'text'|'list'|'facts'|'table'|'image'} type
 * @param {string} titleValue
 * @param {Record<string, any>} payload
 * @param {Record<string, any>} [options]
 */
export function createItem(id, type, titleValue, payload, options = {}) {
  return {
    id,
    type,
    title: text(titleValue),
    caption: text(options.caption),
    note: text(options.note),
    selected: options.selected !== false,
    source: asObject(options.source),
    ...payload
  };
}

/** @param {{id: string, title: string, ordinal: number}} definition @param {any[]} items */
export function createChapter(definition, items) {
  const cleanItems = asArray(items).filter(Boolean);
  return {
    ...definition,
    defaultTitle: definition.title,
    note: '',
    selected: cleanItems.length > 0,
    items: cleanItems
  };
}

/** @param {unknown} assets @returns {Map<string, Record<string, any>>} */
export function createAssetIndex(assets) {
  return new Map(asArray(assets).map(asset => [text(asset?.id), asObject(asset)]).filter(([id]) => id));
}

/**
 * Creates an image item only when an explicitly linked, usable asset exists.
 * @param {Map<string, Record<string, any>>} assetIndex
 * @param {unknown} assetIdValue
 * @param {Record<string, any>} [options]
 */
export function createAssetItem(assetIndex, assetIdValue, options = {}) {
  const assetId = text(assetIdValue);
  const asset = assetIndex.get(assetId);
  if (!asset || ['failed', 'expired'].includes(text(asset.status || asset.metadata?.status))) return null;
  const metadata = asObject(asset.metadata || parseJsonObject(asset.metadata_json));
  return createItem(`asset:${assetId}`, 'image', options.title || asset.title || '项目成果图', {
    assetId,
    fileUrl: text(asset.fileUrl || asset.url, `/api/assets/${encodeURIComponent(assetId)}/file`),
    mimeType: text(asset.mimeType || asset.mime_type),
    assetType: text(asset.assetType || asset.asset_type),
    metadata
  }, {
    caption: options.caption || metadata.description || '',
    source: options.source || { stage: options.stage || '', assetId }
  });
}
